import { GetListParams } from 'react-admin'
import CFG from 'services/cfg'
import { Memoizer, ProductName, WithCode, WithId } from 'types/common'
import { ActivityStatus } from 'types/form'
import { TimeLabel } from './date'
import { getId } from './common/objects'

export * from './common/index'
export * from './common/objects'
export * from './common/arrays'
export * from './sorting'

export const getDefaultListParams = (): GetListParams => ({
  filter: '',
  pagination: {
    page: 1,
    perPage: CFG.perPageDefault,
  },
  sort: {
    field: '',
    order: '',
  },
})

export const convertEmptyStringToUndefined = <T extends unknown>(
  p: T | undefined | ''
): T | undefined => (p === '' ? undefined : p)

export const getName = <T extends { name: string }>(item: T) => item.name

export const getWithCode = <T extends WithCode>(item: T) => ({
  code: item.code,
})

export const noDecimals = (n: number) => Math.floor(n)

export const getAggregateValue = <T>(
  collection: T[],
  getAddend: (arg: T) => number
) => collection.map(getAddend).reduce((acc, item) => acc + item ?? 0, 0)

export const objArrayIntersection = (...arrays: WithId[][]) => {
  const [first, ...others] = arrays
  return first.reduce(
    (acc, itm) => [
      ...acc,
      ...(others.every((arr) => arr.map(getId).includes(itm.id)) ? [itm] : []),
    ],
    [] as WithId[]
  )
}

export const nonMemoizer: Memoizer = (fn, _deps) => fn()

export const getServiceDiscriminator = (productName: ProductName) =>
  `${productName.charAt(0).toUpperCase() + productName.slice(1)}Configurator`

export const hasSections = (i: { sections: unknown[] }) => !!i.sections.length

export const isActive = <T extends { status: ActivityStatus }>(e: T) =>
  e.status === 'active'

export const sortAscByMaxParticipants = <T extends { maxParticipants: number }>(
  a: T,
  b: T
) => a.maxParticipants - b.maxParticipants

/**
 * Compare 2 Sets and return true if they're equal
 */
export const areSetsEqual = <T>(a: Set<T>, b: Set<T>) =>
  a.size === b.size && Array.from(a).every((value) => b.has(value))

const TIME_LABEL_REGEX = /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/

export const isTimeLabelValid = (tm?: TimeLabel) =>
  tm && TIME_LABEL_REGEX.test(tm)

/**
 * Checks the uniqueness of the specified field inside the elements of an array
 * @param objects the objects to check
 * @param key the key to check
 */
export const isEveryNameOfKeyUnique = <
  TObject extends object,
  SKey extends keyof TObject
>(
  objects: TObject[] | undefined,
  key: SKey
) => {
  const unique = new Set(objects?.map((e) => e[key]))
  return unique.size === objects?.length
}
